import React, { useEffect } from 'react';

import { MenuItem, Select, Typography, Box } from '@mui/material';
import Checkbox from 'components/V2/Checkbox';
import PropTypes from 'prop-types';

import {
    selectSx,
    menuItemSx,
    infoPanelContainerSx,
    infoPanelContentSx,
    infoIconWrapperSx,
    infoTextSx,
    paperSx,
} from './styles';

import { ReactComponent as InfoIcon } from 'asset/IconsPack/hx_info-circle.svg';
import { ReactComponent as DropDown } from 'asset/icons/pagination_dropdown.svg';

import { getGuestyPassCriteria, passCriteriaOptionsInfo } from '../../constants';

const ENABLE_PASS_CRITERIA =
    'Pass criteria depends on your workflow settings. Only the checks you configure will appear in the pass criteria options.';

function SelectPassCriteria({ formik, workflow, setUnsavedChanges, unsavedChanges }) {
    const [selectedInfo, setSelectedInfo] = React.useState('');

    const GUESTY_PASS_CRITERIA = getGuestyPassCriteria(workflow);

    const handleChange = (event) => {
        const {
            target: { value },
        } = event;

        const result = typeof value === 'string' ? value.split(',') : value;
        const falseRemoved = result.filter((item) => item !== false);
        setUnsavedChanges(true);
        formik.setFieldValue('passCriteria', falseRemoved);
    };

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const defaultMenuProps = {
        PaperProps: {
            sx: paperSx(anchorEl ? anchorEl.clientWidth : 0),
        },
        anchorEl: anchorEl,
        keepMounted: true,
        getContentAnchorEl: null,
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
        },
    };

    const menuProps = {
        ...defaultMenuProps,

        PaperProps: {
            ...defaultMenuProps.PaperProps,
        },
        anchorOrigin: {
            ...defaultMenuProps.anchorOrigin,
        },
        transformOrigin: {
            ...defaultMenuProps.transformOrigin,
        },
    };

    useEffect(() => {
        const passCriteria = getGuestyPassCriteria(workflow);

        // only enable existing pass criteria value saved on brand or else from workflow config
        let brandPassCriteria = Object.keys(passCriteriaOptionsInfo);

        // On updating workflow, don't consider saved pass criteria and allow changes from workflow config
        if (!unsavedChanges) {
            brandPassCriteria = formik?.values?.passCriteria;
        }

        const newValue = passCriteria
            .filter((item) => !item.disabled && (brandPassCriteria || [])?.includes(item.value))
            .map((item) => item.value);

        formik.setFieldValue('passCriteria', newValue);
        // eslint-disable-next-line
    }, [workflow]);

    return (
        <Select
            name="passCriteria"
            open={Boolean(anchorEl)}
            sx={selectSx}
            value={formik?.values?.passCriteria}
            MenuProps={menuProps}
            onChange={handleChange}
            placeholder="Select Pass Criteria"
            IconComponent={DropDown}
            onClose={handleClose}
            onOpen={handleOpen}
            multiple
            renderValue={(selected) => {
                return selected
                    .reduce((acc, value) => {
                        const criteria = GUESTY_PASS_CRITERIA.find(
                            (c) => c.value === value && !c.disabled
                        );
                        if (criteria?.label) {
                            acc.push(criteria.label);
                        }
                        return acc;
                    }, [])
                    .join(', ');
            }}
        >
            {GUESTY_PASS_CRITERIA?.map((option) => (
                <MenuItem
                    key={option.value}
                    value={!option?.disabled && option.value}
                    sx={menuItemSx}
                    disabled={option?.disabled}
                    onMouseOver={() =>
                        setSelectedInfo(option?.disabled ? 'disabled' : option.value)
                    }
                >
                    <Checkbox
                        checked={formik.values.passCriteria.indexOf(option.value) > -1}
                        disabled={option?.disabled}
                    />
                    {option.label}
                </MenuItem>
            ))}
            <Box sx={infoPanelContainerSx}>
                <Box sx={infoPanelContentSx}>
                    <Box sx={infoIconWrapperSx}>
                        <InfoIcon />
                    </Box>
                    <Typography sx={infoTextSx}>
                        {selectedInfo === 'disabled'
                            ? ENABLE_PASS_CRITERIA
                            : passCriteriaOptionsInfo[selectedInfo]}
                    </Typography>
                </Box>
            </Box>
        </Select>
    );
}

SelectPassCriteria.propTypes = {
    formik: PropTypes.object,
    workflow: PropTypes.object,
    setUnsavedChanges: PropTypes.func,
    unsavedChanges: PropTypes.bool,
};

export default SelectPassCriteria;
